import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedHistoryEntry } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { howMadeMessages as messages } from '../messages/howMadeMessages';
import { DIRECTED_STEP_NAMES } from '../shell/DirectedStepper';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { isEditedPick, isPickedOption, pickedRoundIndex, storyRounds } from './howMadeRules';
import { HowMadeRounds } from './HowMadeRounds';
import { HowMadeSection } from './HowMadeSection';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for "How it was made"; each part uses its own slice of it */
import styles from './HowMade.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Step 2 of "How it was made" (design l1): the story ideas of the director, round by round. The
 * picked idea has a mark ("Picked · edited" when the artist changed it); the others are dimmed.
 */
const HowMadeStoriesComponent: React.FC<{ entries: readonly DirectedHistoryEntry[] }> = ({ entries }) => {
  const intl = useIntl();
  const { video } = useDirectedVideoContext();
  const { named } = useDirectedCastNames();
  const rounds = useMemo(() => storyRounds(entries, video), [entries, video]);
  const picked = pickedRoundIndex(rounds, video.story);
  const [shown, setShown] = useState<number | null>(null);
  const index = shown ?? picked;
  const round = rounds[index];
  const ideas = rounds.reduce((sum, item) => sum + item.options.length, 0);
  const name = intl.formatMessage(DIRECTED_STEP_NAMES.stories);

  return (
    <HowMadeSection
      step="stories"
      eyebrow={intl.formatMessage(messages.stepEyebrow, { number: 2, name })}
      title={
        video.story === null
          ? intl.formatMessage(messages.noPick)
          : intl.formatMessage(messages.storyTitle, { title: named(video.story.title) })
      }
      text={intl.formatMessage(messages.storyText, { ideas, rounds: Math.max(1, rounds.length) })}
      aside={<HowMadeRounds count={rounds.length} shown={index} picked={picked} onShow={setShown} />}
    >
      {round !== undefined && (
        <div className={styles.options}>
          {round.options.map((story, at) => {
            const isOn = isPickedOption(story, video.story);
            const shownStory = isOn && video.story !== null ? video.story : story;
            return (
              <div
                key={story.id}
                className={clsx(styles.option, isOn ? styles.optionOn : styles.optionDim)}
                data-picked={isOn}
              >
                <div className={styles.optionHead}>
                  <span className={styles.optionNumber}>{intl.formatMessage(messages.idea, { number: at + 1 })}</span>
                  {isOn && (
                    <span className={styles.pick}>
                      {intl.formatMessage(isEditedPick(story, video.story) ? messages.pickedEdited : messages.picked)}
                    </span>
                  )}
                </div>
                <div className={styles.optionTitle}>{named(shownStory.title)}</div>
                <p className={styles.optionText}>{named(shownStory.logline)}</p>
              </div>
            );
          })}
        </div>
      )}
      {round?.note !== null && round?.note !== undefined && (
        <p className={styles.note}>{intl.formatMessage(messages.roundNote, { note: round.note })}</p>
      )}
    </HowMadeSection>
  );
};

export const HowMadeStories = TypedMemo(HowMadeStoriesComponent);
